import { AnimType } from '@tencent/slide-sdk-model';
import { ANIMATION_LIST, ANIM_GALLERY, ANIM_RECENT_ID } from './constants';
import { i18n } from '@src/slide/application/service/edit-extension/edit-extension-export';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IAnimCategoryManager } from '@src/slide/application/service/anim-category-manager/interface';


type IAnimationHistoryItem = {
    id: string,
    label: string,
    children: typeof ANIMATION_LIST
};

interface IAnimationHistory {
    setAnimationHistory(animType: AnimType): void;
    getAllAnimWithHistory(): IAnimationHistoryItem[];
}

type StorageKeyType = LocalStorageKeys.RECENT_ANIMATION_STORAGE;

export type IAnimHistoryProps = {
    storageKey?: StorageKeyType;
    maxCount?: number;
};

const MAX_ANIMATION_HISTORY_COUNT = 5; // 存储最近使用的动画个数

export class AnimationHistory implements IAnimationHistory {
    private static instance: AnimationHistory;

    public static getInstance(props?: IAnimHistoryProps) {
        if (!this.instance) {
            this.instance = new AnimationHistory(props);
        }
        return this.instance;
    }

    private storageKey: StorageKeyType;
    private maxCount: number;
    private validAnimationSet: Set<string>;
    private animCategoryManager: IAnimCategoryManager;

    private constructor(props?: IAnimHistoryProps) {
        const {
            storageKey = LocalStorageKeys.RECENT_ANIMATION_STORAGE,
            maxCount = MAX_ANIMATION_HISTORY_COUNT,
        } = props || {};
        this.storageKey = storageKey;
        this.maxCount = maxCount;
        this.initAnimCategoryManager();
    }


    /**
     * 设置最近使用的动画
     * @param animType 动画类型
     */
    public setAnimationHistory(animType: AnimType): void {
        const animations = this.getAnimationHistory();
        const curAnimIndex = animations.indexOf(animType);
        // 已存在移到第一位，不存在直接放在第一位
        if (curAnimIndex > -1) {
            animations.splice(curAnimIndex, 1);
        }
        const newAnimations = [animType, ...animations.splice(0, this.maxCount - 1)];
        userLocalStorage.setItem(this.storageKey, newAnimations);
    }

    /**
     * 获取所有定义的动画类型和图标，包括最近使用数据
     * @returns IAnimationHistoryItem[]
     */
    public getAllAnimWithHistory(): IAnimationHistoryItem[] {
        const  recentAnimation  = this.getAnimationHistory();
        if (!recentAnimation.length) return ANIM_GALLERY;

        const list = recentAnimation.map(id => ANIMATION_LIST.find(anim => anim.id === id)).filter(anim => !!anim);
        return (
            this.animCategoryManager?.getAnimCategoryList([
                {
                    id: ANIM_RECENT_ID,
                    label: i18n.t('最近使用'),
                    children: list,
                },
                ...ANIM_GALLERY,
            ]) ?? []
        );
    }

    /**
     * 获取最近使用的动画名称
     * @returns AnimType[]
     */
    private getAnimationHistory(): AnimType[] {
        const animationHistory = userLocalStorage.getItem(this.storageKey, []);

        if (!this.validAnimationSet?.size)  {
            this.validAnimationSet = new Set(ANIMATION_LIST.map(anim => anim.id));
        }
        // 过滤掉不支持的动画类型
        return animationHistory.filter(anim => this.validAnimationSet.has(anim));
    }

    /**
     * 初始化animCategoryManager
     */
    private initAnimCategoryManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
        const animCategoryManager = appServices.editableServiceLayer?.animCategoryManager?.dangerousInstance;
        if (animCategoryManager) {
            this.animCategoryManager = animCategoryManager;
        } else {
            appServices.editableServiceLayer?.animCategoryManager?.promise().then((animCategoryManager) => {
                this.animCategoryManager = animCategoryManager;
            });
        }
    }
}
